import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import moment from 'moment';
import { JobType } from 'shared/common/job-constants';
import { useLocalStorage } from '../../hooks/use-local-storage';
import {
    Duration,
    DurationOptionLabel,
    getConversionPeriodDuration,
    getTrendPeriodDuration
} from './metrics-durations';

const defaultConversionPeriodOpt: DurationOptionLabel = '30 days (ending 2 weeks ago)';
const defaultTrendPeriodOpt: DurationOptionLabel = 'Last 6 months';
const pollInterval = 300000;

const getMonthDateRange = () =>
    [moment().startOf('month').valueOf(), moment().endOf('day').valueOf()] as [number, number];

const getYearDateRange = () =>
    [moment().startOf('year').valueOf(), moment().endOf('day').valueOf()] as [number, number];

// Rolling trend defaults
// tslint:disable:no-magic-numbers
const defaultTrendRollingWindowMonths: number | null = null;
const defaultTrendRollingTickMonths = 1;
const defaultTrendUseCalendarMonthTotals = false;
// tslint:enable:no-magic-numbers

const MetricsDashboardDataContext = createContext<{
    conversionDuration: { label: DurationOptionLabel; value: Duration };
    trendDuration: { label: DurationOptionLabel; value: Duration };
    trendRollingTickMonths: number;
    trendRollingWindowMonths: number | null;
    trendUseCalendarMonthTotals: boolean;
    onConversionDurationChange: (label: DurationOptionLabel, value: Duration) => void;
    onTrendDurationChange: (label: DurationOptionLabel, value: Duration) => void;
    monthDateRange: [number, number];
    yearDateRange: [number, number];
    restrictToAccountManagerIds: string[];
    restrictToClientIds: string[];
    restrictToRecruiterIds: string[];
    restrictToTeam: { id: string; users: string[]; role: 'recruiter' | 'account_manager' };
    jobTypes: JobType[];
    setJobTypes: (jobTypes: JobType[]) => void;
    setRestrictToAccountManagerIds: (ids: string[]) => void;
    setRestrictToClientIds: (ids: string[]) => void;
    setRestrictToRecruiterIds: (ids: string[]) => void;
    setRestrictToTeam: (team: { id: string; users: string[]; role: 'recruiter' | 'account_manager' }) => void;
    setTrendRollingTickMonths: (months: number) => void;
    setTrendRollingWindowMonths: (months: number | null) => void;
    setTrendUseCalendarMonthTotals: (useCalendarMonthTotals: boolean) => void;
}>({
    conversionDuration: undefined,
    jobTypes: undefined,
    monthDateRange: undefined,
    onConversionDurationChange: () => {
        /* */
    },
    onTrendDurationChange: () => {
        /* */
    },
    restrictToAccountManagerIds: undefined,
    restrictToClientIds: undefined,
    restrictToRecruiterIds: undefined,
    restrictToTeam: undefined,
    setJobTypes: () => {
        /* */
    },
    setRestrictToAccountManagerIds: () => {
        /* */
    },
    setRestrictToClientIds: () => {
        /* */
    },
    setRestrictToRecruiterIds: () => {
        /* */
    },
    setRestrictToTeam: () => {
        /* */
    },
    setTrendRollingTickMonths: () => {
        /* */
    },
    setTrendRollingWindowMonths: () => {
        /* */
    },
    setTrendUseCalendarMonthTotals: () => {
        /* */
    },
    trendDuration: undefined,
    trendRollingTickMonths: defaultTrendRollingTickMonths,
    trendRollingWindowMonths: defaultTrendRollingWindowMonths,
    trendUseCalendarMonthTotals: defaultTrendUseCalendarMonthTotals,
    yearDateRange: undefined
});

interface MetricsDashboardDataProviderProps {
    children?: ReactNode;
}

const MetricsDashboardDataProvider = ({ children }: MetricsDashboardDataProviderProps): JSX.Element => {
    const [monthDateRange, setMonthDateRange] = useState(getMonthDateRange());
    const [yearDateRange, setYearDateRange] = useState(getYearDateRange());
    const [restrictToAccountManagerIds, setRestrictToAccountManagerIds] = useLocalStorage<string[]>(
        'metrics-dash-restrict-to-am',
        undefined
    );
    const [restrictToClientIds, setRestrictToClientIds] = useLocalStorage<string[]>(
        'metrics-dash-restrict-to-client',
        undefined
    );
    const [restrictToRecruiterIds, setRestrictToRecruiterIds] = useLocalStorage<string[]>(
        'metrics-dash-restrict-to-recruiter',
        undefined
    );
    const [conversionPeriodLabel, setConversionPeriodLabel] = useLocalStorage<DurationOptionLabel>(
        'metrics-dash-conversion-period',
        defaultConversionPeriodOpt
    );
    const [conversionPeriodCustom, setConversionPeriodCustom] = useLocalStorage<Duration>(
        'metrics-dash-conversion-period-custom',
        undefined
    );
    const [trendPeriodLabel, setTrendPeriodLabel] = useLocalStorage<DurationOptionLabel>(
        'metrics-dash-trend-period',
        defaultTrendPeriodOpt
    );
    const [trendPeriodCustom, setTrendPeriodCustom] = useLocalStorage<Duration>(
        'metrics-dash-trend-period-custom',
        undefined
    );
    const [trendRollingWindowMonths, setTrendRollingWindowMonths] = useLocalStorage<number | null>(
        'metrics-dash-trend-rolling-window-months',
        defaultTrendRollingWindowMonths
    );
    const [trendRollingTickMonths, setTrendRollingTickMonths] = useLocalStorage<number>(
        'metrics-dash-trend-rolling-tick-months',
        defaultTrendRollingTickMonths
    );
    const [trendUseCalendarMonthTotals, setTrendUseCalendarMonthTotals] = useLocalStorage<boolean>(
        'metrics-dash-trend-use-calendar-month-totals',
        defaultTrendUseCalendarMonthTotals
    );
    const [restrictToTeam, setRestrictToTeam] = useLocalStorage<{
        id: string;
        users: string[];
        role: 'recruiter' | 'account_manager';
    }>('metrics-dash-restrict-to-team', undefined);
    const [jobTypes, setJobTypes] = useLocalStorage<JobType[]>('metrics-dash-job-types', [
        'C',
        'S',
        'CnC',
        'R',
        'C+$',
        'SwC',
        'CnC+$',
        'RPO'
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            // update date ranges
            setMonthDateRange(getMonthDateRange());
            setYearDateRange(getYearDateRange());
        }, pollInterval);
        return () => clearInterval(interval);
    }, []);

    // Normalize redundant state: lookback equal to tick behaves the same as "None (per tick)".
    useEffect(() => {
        if (trendUseCalendarMonthTotals) {
            return;
        }
        if (trendRollingWindowMonths !== null && trendRollingWindowMonths === trendRollingTickMonths) {
            setTrendRollingWindowMonths(null);
        }
    }, [setTrendRollingWindowMonths, trendRollingTickMonths, trendRollingWindowMonths, trendUseCalendarMonthTotals]);

    const conversionDuration =
        conversionPeriodLabel === 'Custom'
            ? conversionPeriodCustom
            : getConversionPeriodDuration(conversionPeriodLabel);

    const trendDuration = trendPeriodLabel === 'Custom' ? trendPeriodCustom : getTrendPeriodDuration(trendPeriodLabel);

    const handleConversionDurationChange = (label: DurationOptionLabel, value: Duration) => {
        setConversionPeriodLabel(label);
        if (label === 'Custom') {
            setConversionPeriodCustom(value);
        }
    };

    const handleTrendDurationChange = (label: DurationOptionLabel, value: Duration) => {
        setTrendPeriodLabel(label);
        if (label === 'Custom') {
            setTrendPeriodCustom(value);
        }
    };

    const contextValue = {
        conversionDuration: { label: conversionPeriodLabel, value: conversionDuration },
        jobTypes,
        monthDateRange,
        onConversionDurationChange: handleConversionDurationChange,
        onTrendDurationChange: handleTrendDurationChange,
        restrictToAccountManagerIds,
        restrictToClientIds,
        restrictToRecruiterIds,
        restrictToTeam,
        setJobTypes,
        setRestrictToAccountManagerIds,
        setRestrictToClientIds,
        setRestrictToRecruiterIds,
        setRestrictToTeam,
        setTrendRollingTickMonths,
        setTrendRollingWindowMonths,
        setTrendUseCalendarMonthTotals,
        trendDuration: { label: trendPeriodLabel, value: trendDuration },
        trendRollingTickMonths,
        trendRollingWindowMonths,
        trendUseCalendarMonthTotals,
        yearDateRange
    };

    return <MetricsDashboardDataContext.Provider value={contextValue}>{children}</MetricsDashboardDataContext.Provider>;
};

const useMetricsDashboardData = () => useContext(MetricsDashboardDataContext);

export { MetricsDashboardDataProvider, useMetricsDashboardData, pollInterval };
